import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import ConfirmContext from 'contexts/ConfirmContext';
import { destroy, initialize,change,updateSyncErrors } from 'redux-form';
import { useMutation } from '@apollo/react-hooks';

import {
  AppHeader,
  CircularLoader,
  TabsMenu,
  TabsPanel,
  RadioBtnTabsMenu,
  RentalItemsGeneralCreate,
  RentalGeneralCreateSecondLanguage,
  RentalDescriptionCreate,
  RentalPricesCreate,
  AddOns,
  RentedOutDates
} from 'components';

import {
  CREATE_RENTAL_ITEM,
  UPDATE_RENTAL_ITEM
} from 'graphql/mutations/rental-items';

import { isRentalFormDone } from 'utils/rental-items/rentalFormDoneHandler';
import { onGeneralAndDescFormsHandler } from 'utils/accommodations-and-activities/generalAndDescriptionFormsHandler';
import {isNextStepAvailable, isCurrentStepAvailable} from 'utils/rental-items/rentalItemsNextStepAvailableHandler';
import {getPreparedDataToCreateRentalItems} from'utils/rental-items/prepareRentalItemsData';
import {
  addAddOnsLanguage,
  deleteAddOnsLanguage
} from 'utils/accommodations-and-activities/addOnsLanguagesHandler';

import {
  clearDeletedFields,
} from 'actions/activityAndAccommodation';

import {
  RENTAL_ITEMS_FORMS_NUMBERS,
  RENTAL_ITEMS_FORMS_ORDER,
  DEFAULT_LANGUAGE,
  INITIAL_RENTAL_ITEMS_FORM_DATA,
  RENTAL_ITEMS_TAB
} from './constants';


import { useStyles } from './styles';

const CreateRentalItems = ({ 
  data,
  pricesActiveSubTabNumber,
  loading,
  ...props }) => {
  const classes = useStyles();
  const [t] = useTranslation();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const confirm = React.useContext(ConfirmContext);

  const [createRentalItem] = useMutation(CREATE_RENTAL_ITEM);
  const [updateRentalItem] = useMutation(UPDATE_RENTAL_ITEM);

  const [isLoading, setIsLoading] = React.useState(false);
  const [isUpdate, setIsUpdate] = React.useState(false);
  const [submitButton, setSubmitButton] = React.useState('');
  const [pricesActiveSubTab, setPricesActiveSubTab] = React.useState(0);
  const [isSaveUnsavedChanges, setIsSaveUnsavedChanges] = React.useState(false);
  const [isVisitedPrices] = React.useState(false);


  const [activeTab, setActiveTab] = React.useState(0);
  const [selectedLanguage, setSelectedLanguage] = React.useState(DEFAULT_LANGUAGE);
  const [languages, setLanguages] = React.useState([DEFAULT_LANGUAGE]);
  const { 
    initializeForm, 
    destroyForm,
    submitForm,
    deletedAddOns,
    changeFormFieldValue,
    updateFormErrors,
    clearDeletedFields
   } = props;

  const [initialFormData, setInitialFormData] = React.useState({
    ...INITIAL_RENTAL_ITEMS_FORM_DATA
  });
  const [savedForms, setSavedForms] = React.useState({});


  React.useEffect(() => {
    clearDeletedFields();
    destroySecondLanguageForms();
    setLanguages([DEFAULT_LANGUAGE]);
    setInitialFormData({ ...INITIAL_RENTAL_ITEMS_FORM_DATA });

    if (loading) {
      setIsLoading(true);
      return;
    }

    if (data) {
      setIsUpdate(true);
      setSavedForms({
        ...data,
        pricesTab:
          RENTAL_ITEMS_FORMS_ORDER[RENTAL_ITEMS_FORMS_NUMBERS.pricesForm][
            pricesActiveSubTabNumber
          ]
      });
      setPricesActiveSubTab(pricesActiveSubTabNumber);
    }

    let newInitialFormData = {
      ...INITIAL_RENTAL_ITEMS_FORM_DATA,
      ...data
    };

    setInitialFormData(newInitialFormData);
    initializeForms(newInitialFormData);
    setIsLoading(false);
  }, [loading, data, pricesActiveSubTabNumber]);

  const destroySecondLanguageForms = () => {
    const forms = ['generalForm-Norwegian', 'descriptionForm-Norwegian'];
    destroyForm(forms);
  };

  const initializeForms = initialFormsData => {
    if (
      initialFormsData['generalForm-Norwegian'] &&
      initialFormsData['generalForm-Norwegian'].title
    ) {
      setLanguages([...languages, 'Norwegian']);
    }

    Object.keys(initialFormsData).map(field => {
      updateFormErrors(field, {});
      initializeForm(field, initialFormsData[field]|| {});
    });
  };

  const onActiveTabChangeHandler = (event, newActiveTab) => {
    if (
          !isNextStepAvailable(
            props,
            languages,
            newActiveTab,
            savedForms,
            pricesActiveSubTab
          )
        ) {
          enqueueSnackbar(
            t(
              'You should complete and save all previous steps before you can go to this step'
            ),
            {
              variant: 'info',
              autoHideDuration: 5000
            }
          );
          return false;
        }
    
        let formName = RENTAL_ITEMS_FORMS_ORDER[activeTab];
    
        if (activeTab === RENTAL_ITEMS_FORMS_NUMBERS.pricesForm) {
          formName = formName[pricesActiveSubTab];
        }         
    setActiveTab(newActiveTab);
  };

   const showSnackbarToFillAllField = () => {
    enqueueSnackbar(
      t(
        'Please fill in all the required fields correctly on the current step for all languages'
      ),
      {
        variant: 'warning'
      }
    );
  };

  const onGeneralAndDescFormSubmitHandler = (
      languagesLength,
      secondLanguage,
      currentFormName,
      secondFormName
    ) => {
      const saved = onGeneralAndDescFormsHandler(
        props,
        initialFormData,
        savedForms,
        languagesLength,
        secondLanguage,
        secondFormName,
        currentFormName,
        isVisitedPrices,
        isSaveUnsavedChanges,
        null,
        showSnackbarToFillAllField,
        null
      );
      
      if (!saved) {
        return;
      }
  
      setSavedForms({
        ...saved
      });
  
      if (submitButton === 'save' && !isSaveUnsavedChanges) {
        onCreateActivityHandler();
      }
      if (submitButton === 'next' && !isSaveUnsavedChanges) {
        setActiveTab(RENTAL_ITEMS_FORMS_NUMBERS[secondFormName]);
      }
    };

  const onCreateActivityHandler = async () => {
       const activePricesForm =
         props[
           RENTAL_ITEMS_FORMS_ORDER[RENTAL_ITEMS_FORMS_NUMBERS.pricesForm][
             pricesActiveSubTab
           ] 
         ];
   
      if (activePricesForm.syncErrors) {
        enqueueSnackbar(
          t(
            'You should complete and save all previous steps before you can save'
          ),
          {
            variant: 'info',
            autoHideDuration: 5000
          }
        );
        return;
      }
      setIsLoading(true);
  
      let data = getPreparedDataToCreateRentalItems(
        props,
        pricesActiveSubTab,
        languages,
        isUpdate,
        deletedAddOns,
      );
     
  
      if (isUpdate) {
        
        updateRentalItem({
          variables: {
            ...data,
            
          }
        }).then(
          () => {
            enqueueSnackbar(t('Rental Item has been successfully updated'), {
              variant: 'success'
            });
            history.push({
              pathname: '/accommodations-and-activities',
              state: { tabNumber: RENTAL_ITEMS_TAB }
            });
          },
          () => {
            enqueueSnackbar(t('Something was wrong'), {
              variant: 'error'
            });
            setIsLoading(false);
          }
        );
        return;
      }
  
  
      createRentalItem({
        variables: {
          ...data,
        }
      }).then(
        () => {
          enqueueSnackbar(
            t(
              `Rental Items has been successfully ${
                'created'
              }`
            ),
            {
              variant: 'success'
            }
          );
          history.push({
            pathname: '/accommodations-and-activities',
            state: { tabNumber: RENTAL_ITEMS_TAB }
          });
        },
        () => {
          enqueueSnackbar(t('Something was wrong'), {
            variant: 'error'
          });
          setIsLoading(false);
        }
      );
    };

  const deleteLanguage = deletedItem => {
    confirm({
      title: 'Are you sure you want to delete this tab?',
      description: 'All related tabs in this language will also be deleted.',
      confirmationText: 'Delete',
      cancellationText: 'Back'
    })
      .then(() => {
        const languagesCopy = [...languages];
        const updatedLanguages = languagesCopy.filter(
          item => item !== deletedItem
        );
        setSelectedLanguage(DEFAULT_LANGUAGE);
        setLanguages(updatedLanguages);
        deleteAddOnsLanguage(
                  props,
                  deletedItem,
                  changeFormFieldValue,
                  activeTab,
                  setActiveTab,
                  setSavedForms,
                  savedForms,
                  updateFormErrors,
                  submitForm
                );

        deleteFormLanguage(deletedItem);
      })
      .catch(() => {});
  };

  const deleteFormLanguage = deletedLanguage => {
    const general = `generalForm-${deletedLanguage}`;
    const description = `descriptionForm-${deletedLanguage}`;

    destroyForm([general, description]);
    setSavedForms({
      ...savedForms,
      [general]: {},
      [description]: {}
    });
  };

  const getPropsForTabsWithLanguages = () => {
    return {
      selectedLanguage: selectedLanguage,
      languages: languages,
      addLanguage: newItem => {
              setLanguages([...languages, newItem]);
              addAddOnsLanguage(
                props,
                newItem,
                changeFormFieldValue,
                updateFormErrors,
                setSavedForms,
                savedForms
              );
            },
      deleteLanguage: deleteLanguage,
      selectLanguage: item => setSelectedLanguage(item)
    };
  };


  const getGeneralCreateTab = () => {
    const languagesLength = languages.length;
    const secondLanguage = languages[languagesLength - 1];

    const generalFormProps = {
      onSubmit: () =>
        onGeneralAndDescFormSubmitHandler(
          languagesLength,
          secondLanguage,
          'generalForm',
          'descriptionForm'
        ),
      ...getPropsForTabsWithLanguages()
    };

    return (
      <div>
        <div hidden={selectedLanguage !== DEFAULT_LANGUAGE}>
          <RentalItemsGeneralCreate
            {...generalFormProps}
            initialValues={INITIAL_RENTAL_ITEMS_FORM_DATA.generalForm}
            isUpdate={isUpdate}
            setSubmitButton={setSubmitButton}
          />
        </div>
        {languagesLength > 1 && (
          <div hidden={selectedLanguage === DEFAULT_LANGUAGE}>
           <RentalGeneralCreateSecondLanguage
              {...generalFormProps}
              form={`generalForm-${secondLanguage}`}
              isUpdate={isUpdate}
              setSubmitButton={setSubmitButton}
            />
          </div>
        )}
      </div>
    );
  };


  const getDescriptionCreateTab = () => {
    const languagesLength = languages.length;
    const secondLanguage = languages[languagesLength - 1];

    const descriptionFormProps = {
      onSubmit: () =>
        onGeneralAndDescFormSubmitHandler(
          languagesLength,
          secondLanguage,
          'descriptionForm',
          'pricesForm'
        ),
      ...getPropsForTabsWithLanguages()
    };

    return (
      <div>
        <div hidden={selectedLanguage !== DEFAULT_LANGUAGE}>
          <RentalDescriptionCreate {...descriptionFormProps} 
            isUpdate={isUpdate}
            setSubmitButton={setSubmitButton}
          />
        </div>
        {languagesLength > 1 && (
          <div hidden={selectedLanguage === DEFAULT_LANGUAGE}>
            <RentalDescriptionCreate
              {...descriptionFormProps}
              form={`descriptionForm-${secondLanguage}`}
              isUpdate={isUpdate}
              setSubmitButton={setSubmitButton}
            />
          </div>
        )}
      </div>
    );
  };

  const tabs = [
    {
      name: 'General',
      formName: 'generalForm',
      component: getGeneralCreateTab()
    },
    {
      name: 'Description',
      formName: 'descriptionForm',
      component: getDescriptionCreateTab()
    },
    {
      name: 'Prices',
      formName: 'pricesForm',
      component: (
       <RentalPricesCreate
          onSubmit={(values, formName) => { 
            setSavedForms({
              ...savedForms,
              [formName]: { ...values },
              pricesTab: formName
            });
             if (submitButton === 'save' && !isSaveUnsavedChanges) {
                    onCreateActivityHandler();
                  }
              if (submitButton === 'next' && !isSaveUnsavedChanges) {
                     setActiveTab(RENTAL_ITEMS_FORMS_NUMBERS.rentedOutDatesForm);
                    }
          }}
          selectedTabIndex={pricesActiveSubTab}
            isUpdate={isUpdate}
            setSubmitButton={setSubmitButton}
        />
      ),
      subTabs: [
        {
          title: 'Fixed prices',
          instruction: 'One price for rental items'
        },
        {
          title: 'Daily prices',
          instruction: 'Prices per each day'
        },
        {
          title: 'Week Based prices',
          instruction: 'Separate for weekday and weekend'
        },
        {
          title: 'Hourly Based prices',
          instruction: ' per hour prices '
        },
      ]
    },
    {
      name: 'Out Of Order Dates',
      formName: 'rentedOutDatesForm',
      component: (
        <RentedOutDates
          onSubmit={values => {
            setSavedForms({ ...savedForms, rentedOutDatesForm: { ...values } });
            if (submitButton === 'save' && !isSaveUnsavedChanges) {
              onCreateActivityHandler();
            }
            if (submitButton === 'next' && !isSaveUnsavedChanges) {
              setActiveTab(RENTAL_ITEMS_FORMS_NUMBERS.addOnsForm);
            }
          }}
          isUpdate={isUpdate}
          setSubmitButton={setSubmitButton}
        />
      )
    },
    {
      name: 'Add ons',
      formName: 'addOnsForm',
      component: (
        <AddOns
          {...getPropsForTabsWithLanguages()}
          onSubmit={values => {
            setSavedForms({ ...savedForms, addOnsForm: { ...values } });
             onCreateActivityHandler();
          }}
            isUpdate={isUpdate}
            setSubmitButton={setSubmitButton}
            actionLabel="Save"
        />
      )
    },
  ];

  return (
    <>
      <AppHeader />
      <CircularLoader
        isLoading={isLoading}
        loaderClassName={classes.loaderClassName}
      />
      <div className={classes.root}>
        <TabsMenu
          activeTab={activeTab}
          title={
            isUpdate 
              ? 'Edit Rental Item'
              : 'Create new Rental Item'
          }
          isDoneTab={tab => {
            return isRentalFormDone(
              tab.formName,
              languages,
              props,
              savedForms,
              activeTab,
              pricesActiveSubTab
            );
          }}
          tabs={tabs}
          change={onActiveTabChangeHandler}
        />

        <RadioBtnTabsMenu
          activeTab={activeTab}
          activeSubTab={pricesActiveSubTab}
          tabs={tabs}
          change={e => setPricesActiveSubTab(parseInt(e.target.value))}
        />

        <div className={classes.content}>
          <div className={classes.contentBox}>
            <TabsPanel tabs={tabs} activeTab={activeTab} />
          </div>
        </div>
      </div>
    </>
  );
};


const mapStateToProps = state => {
  return {
    ...state.form,
    businessOwner: state.businessOwner,
    deletedAddOns: state.activityAndAccommodation.deletedAddOns,
  
  };
};
const mapDispatchToProps = dispatch => ({
  destroyForm: forms => {
    dispatch(destroy(...forms));
  },
  initializeForm: (form, data) => {
    dispatch(initialize(form, data));
  },
  changeFormFieldValue: (form, field, value) => {
      dispatch(change(form, field, value));
    },
  clearDeletedFields: () => {
    dispatch(clearDeletedFields());
  },
  updateFormErrors: (form, fields) => {
      dispatch(updateSyncErrors(form, fields));
    },
  
});

export default connect(mapStateToProps, mapDispatchToProps)(CreateRentalItems);